import type { PurchaseRequest, PurchaseRequestStatus, Document, DocumentType, PurchaseRequestLine, PurchaseRequestLineType, ApprovalHistoryEntry } from '@/types/purchase-request';

const suppliers = [
  'Acme Corp',
  'Global Supplies Ltd',
  'Tech Solutions Inc',
  'Office Essentials',
  'Professional Services Co',
  'Digital Systems',
  'Enterprise Solutions',
  'Quality Products Ltd',
  'Innovation Partners',
  'Strategic Vendors Inc',
];

const categories = [
  'Software Licenses',
  'Hardware Equipment',
  'Professional Services',
  'Office Supplies',
  'Marketing Materials',
  'Training & Development',
  'Travel & Accommodation',
  'Consulting Services',
  'Maintenance & Support',
  'Research & Development',
];

const requesters = [
  'John Smith',
  'Sarah Johnson',
  'Michael Brown',
  'Emily Davis',
  'David Wilson',
  'Lisa Anderson',
  'James Taylor',
  'Jennifer Martinez',
];

const approvers = [
  'Robert Thompson',
  'Patricia White',
  'Christopher Lee',
  'Amanda Clark',
];

const approvalStages = [
  'Manager Approval',
  'Finance Approval',
  'Director Approval',
  'CFO Approval',
  'Procurement Review',
];

const approvalActions = [
  'Submitted',
  'Approved',
  'Rejected',
  'Redirected',
  'Resubmitted',
];

const currencies = ['GBP', 'GBP', 'GBP', 'GBP', 'GBP', 'GBP', 'EUR', 'EUR', 'EUR', 'USD', 'USD']; // 60% GBP, 25% EUR, 15% USD

const costCentres = [
  { code: 'CC-001', name: 'Development' },
  { code: 'CC-002', name: 'Marketing' },
  { code: 'CC-003', name: 'Operations' },
  { code: 'CC-004', name: 'Sales' },
  { code: 'CC-005', name: 'IT Infrastructure' },
  { code: 'CC-006', name: 'Human Resources' },
  { code: 'CC-007', name: 'Finance' },
  { code: 'CC-008', name: 'Customer Support' },
  { code: 'CC-009', name: 'Research & Development' },
  { code: 'CC-010', name: 'Administration' },
];

const lineDescriptions = [
  'Software license - annual subscription',
  'Hardware equipment - laptop',
  'Professional services - consulting',
  'Office supplies - stationery',
  'Marketing materials - brochures',
  'Training course - online',
  'Travel expenses - conference',
  'Maintenance contract - annual',
  'Cloud services - monthly',
  'Equipment rental - projector',
  'Design services - logo',
  'Support contract - premium',
  'Data storage - cloud backup',
  'Security software - endpoint protection',
  'Communication tools - video conferencing',
];

const taxCodes = ['S', 'Z', 'E', 'R', 'V']; // Standard, Zero, Exempt, Reduced, VAT

const glAccounts = [
  '4000-100',
  '4000-200',
  '5000-100',
  '5000-200',
  '6000-100',
  '6000-200',
  '7000-100',
  '7000-200',
  '8000-100',
  '8000-200',
];

const lineNotes = [
  'Urgent delivery required',
  'Approved by department head',
  'Part of annual budget allocation',
  'Replacement for existing equipment',
  'New project requirement',
  'Vendor recommended upgrade',
  'Cost savings initiative',
  'Compliance requirement',
  undefined, // Some lines won't have notes
  undefined,
  undefined,
];

const documentNames = [
  'Quote_Document.pdf',
  'Purchase_Order.pdf',
  'Invoice.pdf',
  'Product_Specifications.pdf',
  'Contract_Agreement.pdf',
  'Supplier_Proposal.pdf',
  'Budget_Breakdown.xlsx',
  'Cost_Analysis.xlsx',
  'Product_Image.jpg',
  'Technical_Drawing.png',
  'Approval_Form.docx',
  'Requirements_Document.docx',
];

function generateDocuments(requestId: string): Document[] {
  const documentCount = Math.floor(Math.random() * 5); // 0-4 documents
  if (documentCount === 0) return [];

  const documents: Document[] = [];
  const usedNames = new Set<string>();

  for (let i = 0; i < documentCount; i++) {
    let name = getRandomElement(documentNames);
    // Ensure unique names
    while (usedNames.has(name)) {
      name = getRandomElement(documentNames);
    }
    usedNames.add(name);

    const extension = name.split('.').pop()?.toLowerCase() || 'pdf';
    let type: DocumentType = 'other';
    
    if (extension === 'pdf') type = 'pdf';
    else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) type = 'image';
    else if (['xlsx', 'xls', 'csv'].includes(extension)) type = 'spreadsheet';
    else if (['doc', 'docx', 'txt'].includes(extension)) type = 'document';

    documents.push({
      id: `${requestId}-doc-${i + 1}`,
      name,
      type,
      size: Math.floor(Math.random() * 5000000) + 50000, // 50KB - 5MB
      uploadDate: getRandomDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()),
      url: `/api/documents/${requestId}-doc-${i + 1}`, // Mock URL
    });
  }

  return documents;
}

function generatePurchaseRequestLines(requestId: string, totalAmount: number, currency: string): PurchaseRequestLine[] {
  const lineCount = Math.floor(Math.random() * 4) + 2; // 2-5 lines
  const lines: PurchaseRequestLine[] = [];
  const lineTypes: PurchaseRequestLineType[] = ['Price', 'Stock', 'Text'];
  
  // Distribute the total amount across lines
  let remainingAmount = totalAmount;
  
  for (let i = 0; i < lineCount; i++) {
    const isLastLine = i === lineCount - 1;
    const lineType = getRandomElement(lineTypes);
    
    // For text lines, amount is 0
    if (lineType === 'Text') {
      lines.push({
        id: `${requestId}-line-${i + 1}`,
        lineNumber: i + 1,
        lineType,
        description: 'Additional notes or specifications',
        quantity: 0,
        unitPrice: 0,
        totalAmount: 0,
        currency,
        costCentreCode: getRandomElement(costCentres).code,
        costCentreName: getRandomElement(costCentres).name,
        taxCode: undefined,
        glAccount: undefined,
        notes: getRandomElement(lineNotes),
      });
      continue;
    }
    
    // For the last line, use remaining amount
    const lineAmount = isLastLine 
      ? remainingAmount 
      : Math.round((remainingAmount / (lineCount - i)) * (0.5 + Math.random()) * 100) / 100;
    
    remainingAmount -= lineAmount;
    
    const quantity = Math.floor(Math.random() * 10) + 1;
    const unitPrice = Math.round((lineAmount / quantity) * 100) / 100;
    const costCentre = getRandomElement(costCentres);
    
    lines.push({
      id: `${requestId}-line-${i + 1}`,
      lineNumber: i + 1,
      lineType,
      description: getRandomElement(lineDescriptions),
      quantity,
      unitPrice,
      totalAmount: lineAmount,
      currency,
      costCentreCode: costCentre.code,
      costCentreName: costCentre.name,
      taxCode: getRandomElement(taxCodes),
      glAccount: getRandomElement(glAccounts),
      notes: getRandomElement(lineNotes),
    });
  }
  
  return lines;
}

const descriptions = [
  'Annual software license renewal',
  'New laptop computers for development team',
  'Cloud infrastructure services',
  'Office furniture and equipment',
  'Marketing campaign materials',
  'Professional training courses',
  'Conference attendance and travel',
  'External consulting services',
  'Server maintenance contract',
  'Research and development tools',
  'Project management software',
  'Network security equipment',
  'Design software licenses',
  'Mobile device procurement',
  'Data analytics platform',
  'Customer support system',
  'Video conferencing equipment',
  'Backup and disaster recovery',
  'Quality assurance tools',
  'Documentation services',
];

function getRandomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

function getRandomDate(startDate: Date, endDate: Date): string {
  const start = startDate.getTime();
  const end = endDate.getTime();
  const randomTime = start + Math.random() * (end - start);
  return new Date(randomTime).toISOString();
}

function getRandomAmount(min: number, max: number): number {
  return Math.round((Math.random() * (max - min) + min) * 100) / 100;
}

function generateApprovalHistory(
  status: PurchaseRequestStatus,
  requestDate: string,
  approvedDate: string | undefined,
  requester: string,
  approver: string | undefined
): ApprovalHistoryEntry[] {
  const history: ApprovalHistoryEntry[] = [];
  const startDate = new Date(requestDate);
  
  // Always start with submission
  history.push({
    date: requestDate,
    action: 'Submitted',
    byUser: requester,
    toUser: getRandomElement(approvers),
    stage: 'Manager Approval',
    status: 'Pending',
  });
  
  if (status === 'Approved') {
    // Generate 2-4 approval stages
    const stageCount = Math.floor(Math.random() * 3) + 2; // 2-4 stages
    const usedStages = ['Manager Approval'];
    
    for (let i = 0; i < stageCount; i++) {
      const isLastStage = i === stageCount - 1;
      const daysToAdd = Math.floor(Math.random() * 3) + 1; // 1-3 days between approvals
      const approvalDate = new Date(startDate);
      approvalDate.setDate(approvalDate.getDate() + (i + 1) * daysToAdd);
      
      // Pick a stage that hasn't been used
      let stage = getRandomElement(approvalStages);
      while (usedStages.includes(stage)) {
        stage = getRandomElement(approvalStages);
      }
      usedStages.push(stage);
      
      const currentApprover = i === stageCount - 1 && approver ? approver : getRandomElement(approvers);
      
      history.push({
        date: approvalDate.toISOString(),
        action: 'Approved',
        byUser: currentApprover,
        toUser: isLastStage ? undefined : getRandomElement(approvers),
        stage,
        status: isLastStage ? 'Approved' : 'Pending',
      });
    }
  } else if (status === 'Rejected') {
    // Add 1-2 stages before rejection
    const stageCount = Math.floor(Math.random() * 2) + 1; // 1-2 stages
    
    for (let i = 0; i < stageCount; i++) {
      const isLastStage = i === stageCount - 1;
      const daysToAdd = Math.floor(Math.random() * 2) + 1; // 1-2 days between actions
      const actionDate = new Date(startDate);
      actionDate.setDate(actionDate.getDate() + (i + 1) * daysToAdd);
      
      if (isLastStage) {
        // Final rejection
        history.push({
          date: actionDate.toISOString(),
          action: 'Rejected',
          byUser: getRandomElement(approvers),
          stage: getRandomElement(approvalStages),
          status: 'Rejected',
        });
      } else {
        // Intermediate approval
        history.push({
          date: actionDate.toISOString(),
          action: 'Approved',
          byUser: getRandomElement(approvers),
          toUser: getRandomElement(approvers),
          stage: getRandomElement(approvalStages),
          status: 'Pending',
        });
      }
    }
  } else if (status === 'Pending') {
    // For pending, might have 0-2 intermediate approvals
    const stageCount = Math.floor(Math.random() * 3); // 0-2 stages
    
    for (let i = 0; i < stageCount; i++) {
      const daysToAdd = Math.floor(Math.random() * 2) + 1;
      const actionDate = new Date(startDate);
      actionDate.setDate(actionDate.getDate() + (i + 1) * daysToAdd);
      
      history.push({
        date: actionDate.toISOString(),
        action: 'Approved',
        byUser: getRandomElement(approvers),
        toUser: getRandomElement(approvers),
        stage: getRandomElement(approvalStages),
        status: 'Pending',
      });
    }
  }
  
  return history;
}

// Invoice statuses that come from the finance system (Financial Operations)
// These represent the lifecycle of a Posted PO through to payment.
const invoiceStatuses = [
  { invoiceStatus: 'Not Invoiced',     invoiceMatchedStatus: 'Unmatched',        isCommitmentRelieved: false },  // Posted, no invoice yet         — still committed
  { invoiceStatus: 'Partially Invoiced', invoiceMatchedStatus: 'Partially Matched', isCommitmentRelieved: false }, // Partially billed                — still committed
  { invoiceStatus: 'Invoiced',         invoiceMatchedStatus: 'Matched',          isCommitmentRelieved: false },  // Invoice received, not yet paid  — still committed
  { invoiceStatus: 'Paid',             invoiceMatchedStatus: 'Fully Matched',    isCommitmentRelieved: true  },  // Fully paid                      — commitment relieved
  { invoiceStatus: 'Paid',             invoiceMatchedStatus: 'Fully Matched',    isCommitmentRelieved: true  },  // Extra weight so ~40% of Posted are relieved
];

const goodsReceivedStatuses = ['Not Received', 'Partially Received', 'Fully Received'];

export function generateMockPurchaseRequests(projectId: string, count: number = 30): PurchaseRequest[] {
  const requests: PurchaseRequest[] = [];

  // Weight the statuses to show a realistic project lifecycle:
  //   20% Approved (authorised, not yet sent to finance)
  //   35% Posted   (in finance system — some still committed, some relieved)
  //   30% Pending  (awaiting approval)
  //   10% Rejected
  //    5% Cancelled
  const weightedStatuses: PurchaseRequestStatus[] = [
    ...Array(20).fill('Approved'),
    ...Array(35).fill('Posted'),
    ...Array(30).fill('Pending'),
    ...Array(10).fill('Rejected'),
    ...Array(5).fill('Cancelled'),
  ];

  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 6); // 6 months ago
  const endDate = new Date();

  for (let i = 0; i < count; i++) {
    const status = getRandomElement(weightedStatuses);
    const requestDate = getRandomDate(startDate, endDate);
    const approvedDate = (status === 'Approved' || status === 'Posted')
      ? getRandomDate(new Date(requestDate), endDate)
      : undefined;

    const requestId = `PR-${String(1000 + i).padStart(5, '0')}`;
    const currency = getRandomElement(currencies);
    const amount = getRandomAmount(500, 50000);

    // PO numbers exist for Posted requests (and optionally Approved ones awaiting posting)
    const poNumbers = (status === 'Posted' || (status === 'Approved' && Math.random() > 0.5))
      ? Math.random() > 0.7
        ? [`PO-${String(5000 + i).padStart(5, '0')}`, `PO-${String(5001 + i).padStart(5, '0')}`]
        : [`PO-${String(5000 + i).padStart(5, '0')}`]
      : undefined;

    // Invoice / goods received lifecycle — only applies to Posted PRs
    // These fields come from the finance system (Financial Operations)
    const invoiceData = status === 'Posted' ? getRandomElement(invoiceStatuses) : undefined;
    const goodsReceivedStatus = status === 'Posted'
      ? invoiceData?.isCommitmentRelieved
        ? 'Fully Received'                            // Paid → must have been fully received
        : getRandomElement(goodsReceivedStatuses)     // In-flight — any GRN state
      : undefined;

    const requester = getRandomElement(requesters);
    const approver = (status === 'Approved' || status === 'Posted') ? getRandomElement(approvers) : undefined;
    const costCentre = getRandomElement(costCentres);

    const request: PurchaseRequest = {
      id: `pr-${projectId}-${i + 1}`,
      requestId,
      description: getRandomElement(descriptions),
      status,
      amount,
      supplier: getRandomElement(suppliers),
      date: requestDate,
      projectId,
      requestedBy: requester,
      approvedBy: approver,
      approvedDate,
      category: getRandomElement(categories),
      notes: Math.random() > 0.5 ? 'Additional notes and requirements for this purchase request.' : undefined,
      currency,
      documents: generateDocuments(requestId),
      lines: generatePurchaseRequestLines(requestId, amount, currency),
      poNumber: poNumbers,
      poStatus: status === 'Posted' ? getRandomElement(['Pending', 'Partially Processed', 'Processed'] as const) : undefined,
      costCentreCode: costCentre.code,
      costCentreName: costCentre.name,
      approvalHistory: generateApprovalHistory(status, requestDate, approvedDate, requester, approver),
      // Finance system fields — only present on Posted requests
      invoiceStatus:        invoiceData?.invoiceStatus,
      invoiceMatchedStatus: invoiceData?.invoiceMatchedStatus,
      goodsReceivedStatus,
      isCommitmentRelieved: invoiceData?.isCommitmentRelieved,
      // Invoice reference numbers — present when an invoice has been raised against the PO
      invoiceNumbers: (invoiceData && invoiceData.invoiceStatus !== 'Not Invoiced')
        ? Math.random() > 0.6
          ? [`INV-${String(9000 + i).padStart(5, '0')}`, `INV-${String(9001 + i).padStart(5, '0')}`]
          : [`INV-${String(9000 + i).padStart(5, '0')}`]
        : undefined,
    };

    requests.push(request);
  }

  // Sort by date descending (most recent first)
  return requests.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

// Simulate API delay
export async function fetchPurchaseRequests(projectId: number): Promise<PurchaseRequest[]> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(generateMockPurchaseRequests(String(projectId)));
    }, 1000); // 1 second delay to show loading state
  });
}
